import * as React from 'react';

import * as Leads from 'nutshell-core/leads';

import type {LeadStatus} from 'nutshell-graphql-types';
import type {GetLead_lead_stages_edges as StageEdge} from 'nutshell-graphql-types';

import {PipelineSelectPopover} from '../../../../../../ui/lead/shared/components/pipeline-select-popover';

import {Dialog} from '../../dialog/dialog';
import {ScrollableSelectionContainer} from '../../layout/scrollable-selection-container/scrollable-selection-container';

import {getMoveActionVerb} from './helpers';
import {useDetermineHoverInstantMode} from './hooks/use-hover-instant-mode';
import {Stage} from './stage';
import {StagePopover} from './stage-popover';

type Props = {
    leadId: string;
    pipelineName: string;
    pipelineId: string;
    stages: StageEdge[];
    leadStatus: LeadStatus;
    isHot?: boolean;
    onStageChange?: (stageId: string) => Promise<any>;
    isUpdatingStage?: boolean;
    canAccessSetup: boolean;
};

export function StageCarousel(props: Props) {
    const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null);
    const [changingStageRef, setChangingStageRef] = React.useState<HTMLDivElement | null>(null);
    const [isPipelineSelectorVisible, setIsPipelineSelectorVisible] =
        React.useState<boolean>(false);
    const [popoverStageIndex, setPopoverStageIndex] = React.useState<number | null | undefined>(
        null
    );
    const [popoverAnchorRef, setPopoverAnchorRef] = React.useState<
        HTMLButtonElement | null | undefined
    >(null);
    const [isPopoverHovered, setIsPopoverHovered] = React.useState<boolean>(false);

    const {shouldShowInstantly, handleChildHoverDisplayChange} = useDetermineHoverInstantMode(200);

    const pipelineLabelRef = React.useRef<HTMLElement | null>(null);
    const hidePopoverTimerRef = React.useRef<number | null | undefined>(null);

    const activeIndex = props.stages.findIndex((s) => s.isActive);
    const moveActionVerb = getMoveActionVerb(activeIndex, selectedIndex);

    const handleSelect = (index: number, ref: HTMLDivElement | null) => {
        setSelectedIndex(index);
        if (ref) {
            setChangingStageRef(ref);
        }
    };

    const handleStageChange = () => {
        setChangingStageRef(null);
        if (selectedIndex !== null && props.onStageChange) {
            // @ts-expect-error
            const stageId = props.stages[selectedIndex].node.id;
            props
                .onStageChange(stageId)
                .then(() => {
                    setSelectedIndex(null);
                })
                .then(() => {
                    Nut.updateAppearances();
                });
        } else {
            setSelectedIndex(null);
        }
    };

    const handleChangeStageset = (stagesetId: string) => {
        return Leads.changeStageset(props.leadId, stagesetId).then(() => {
            setIsPipelineSelectorVisible(false);
            location.reload();
        });
        // TODO: handle errors
    };

    const handleCancel = () => {
        setChangingStageRef(null);
        setSelectedIndex(null);
    };

    const handlePopoverVisibilityChange = React.useCallback(
        (index: number) => (shouldShow: boolean, ref?: HTMLButtonElement | null) => {
            if (shouldShow) {
                // Cancel any pending hide timer when showing a new popover
                if (hidePopoverTimerRef.current) {
                    clearTimeout(hidePopoverTimerRef.current);
                    hidePopoverTimerRef.current = null;
                }
                setPopoverStageIndex(index);
                setPopoverAnchorRef(ref);
            } else if (popoverStageIndex === index) {
                // Add a grace period before hiding the popover
                // This allows users to move between stages without the popover disappearing
                // However, if the popover is currently hovered, don't start the timer
                if (isPopoverHovered) {
                    return;
                }
                if (hidePopoverTimerRef.current) {
                    clearTimeout(hidePopoverTimerRef.current);
                }
                // @ts-expect-error
                hidePopoverTimerRef.current = setTimeout(() => {
                    setPopoverStageIndex(null);
                    setPopoverAnchorRef(null);
                    hidePopoverTimerRef.current = null;
                }, 400);
            }
        },
        [popoverStageIndex, isPopoverHovered]
    );

    const handlePopoverHoverChange = React.useCallback((isHovered: boolean) => {
        setIsPopoverHovered(isHovered);

        if (isHovered) {
            // Cancel any pending hide timer when hovering the popover
            if (hidePopoverTimerRef.current) {
                clearTimeout(hidePopoverTimerRef.current);
                hidePopoverTimerRef.current = null;
            }
        } else {
            // When leaving the popover, hide it after a short delay
            if (hidePopoverTimerRef.current) {
                clearTimeout(hidePopoverTimerRef.current);
            }
            // @ts-expect-error
            hidePopoverTimerRef.current = setTimeout(() => {
                setPopoverStageIndex(null);
                setPopoverAnchorRef(null);
                hidePopoverTimerRef.current = null;
                setIsPopoverHovered(false);
            }, 200);
        }
    }, []);

    React.useEffect(() => {
        return () => {
            if (hidePopoverTimerRef.current) {
                clearTimeout(hidePopoverTimerRef.current);
            }
        };
    }, []);

    const initialSelectedIndex = activeIndex >= 0 ? activeIndex : 0;

    // ⚠️ The wrapping element cannot be a fragment, otherwise the parent flex
    // container will add more gap
    return (
        <div>
            {/* This is a hack to allow our jQuery layer to still find and know about the selected stage. See use-get-lead-markup.jsx for more details. */}
            <div className='stages-carousel-wrapper display-none'>
                <div
                    className='ui-lead-stage--selected'
                    // @ts-expect-error
                    data-stage-id={props.stages[initialSelectedIndex].node.id}
                ></div>
            </div>
            <ScrollableSelectionContainer
                headerProps={{
                    label: props.pipelineName,
                    hasControls: true,
                }}
                onLabelClick={() => setIsPipelineSelectorVisible(true)}
                getLabelRef={(ref) => {
                    pipelineLabelRef.current = ref;
                }}
                items={props.stages}
                initialSelectedIndex={initialSelectedIndex}
                onSelect={handleSelect}
                generateKey={(item) => item.node.id}
                gap={4}
                renderItem={(item, index) => {
                    const stage = item.node;
                    const isCurrentStage = index === activeIndex;
                    const isActive = activeIndex >= 0 && index <= activeIndex;
                    const isHot = props.isHot;
                    const stageName = stage.name;
                    const isSelected = selectedIndex === index;
                    const isLoading = isSelected && props.isUpdatingStage;

                    return (
                        <Stage
                            stageName={stageName}
                            isActive={isActive}
                            isCurrentStage={isCurrentStage}
                            isSelected={isSelected}
                            isHot={isHot}
                            isStart={index === 0}
                            isEnd={index === props.stages.length - 1}
                            isLoading={isLoading}
                            status={props.leadStatus}
                            // Keep the hover instant if we're showing a popover
                            // in the grace period where we aren't hovering over
                            // a stage
                            isHoverInstant={shouldShowInstantly || popoverStageIndex !== null}
                            onHoverDisplayChange={(isShown) =>
                                handleChildHoverDisplayChange(isShown)
                            }
                            onPopoverVisibilityChange={handlePopoverVisibilityChange(index)}
                        />
                    );
                }}
            />
            {changingStageRef && !props.isUpdatingStage && (
                <Dialog
                    anchor={changingStageRef}
                    location='top'
                    question={`Are you sure you want to ${moveActionVerb} to this stage?`}
                    primaryText={`Yes, ${moveActionVerb}`}
                    tertiaryText='No, cancel'
                    onPrimary={handleStageChange}
                    primaryVariant='save'
                    onTertiary={handleCancel}
                    onBlur={handleCancel}
                    tertiaryVariant='secondary'
                    customStyles={{width: '230px'}}
                />
            )}
            {isPipelineSelectorVisible && (
                <PipelineSelectPopover
                    selectedStagesetId={props.pipelineId}
                    onChangeStageset={handleChangeStageset}
                    onClose={() => setIsPipelineSelectorVisible(false)}
                    anchor={pipelineLabelRef.current}
                />
            )}
            {popoverStageIndex != null && popoverAnchorRef && (
                <StagePopover
                    // @ts-expect-error
                    stage={props.stages[popoverStageIndex]}
                    isPopoverStageActive={activeIndex >= 0 && popoverStageIndex <= activeIndex}
                    popoverAnchorRef={popoverAnchorRef}
                    onPopoverHoverChange={handlePopoverHoverChange}
                    pipelineId={props.pipelineId}
                    canAccessSetup={props.canAccessSetup}
                />
            )}
        </div>
    );
}
